import React, { useEffect, useState } from 'react';

import { Input, InputProps } from './Input';

export interface ParsedInputProps<ParsedValueT extends string | number> extends InputProps {
  valueParser: (value: string | number) => ParsedValueT;
  onValueChange: (value: ParsedValueT) => void;
  /** This option allows onValueChange to NOT be called immediately for input values included in this array.
   *  Default behavior is to not immediately call onValueChange until user clicks elsewhere (onBlur) for empty inputs. */
  ignoreInputWhileEditing?: string[];
}

export function ParsedInput<ParsedValueT extends string | number>({
  valueParser,
  onValueChange,
  ignoreInputWhileEditing = [''],
  ...props
}: ParsedInputProps<ParsedValueT>) {
  // use internal state so we let the user type freely without overriding with parsed value
  const [editValue, setEditValue] = useState(props.value);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (!isFocused || props.disabled) {
      setEditValue(props.value);
    }
    if (props.disabled) {
      setIsFocused(false);
    }
  }, [props.value, isFocused, props.disabled]);

  return (
    <Input
      {...props}
      value={isFocused ? editValue : props.value}
      onChange={(ev) => {
        const { value } = ev.target;
        setEditValue(value);
        // allow user to backspace to empty so they can enter a new value, or ignore certain temporary values like '0'
        if (!ignoreInputWhileEditing.includes(value)) {
          onValueChange(valueParser(value));
        }
      }}
      onFocus={() => {
        setIsFocused(true);
      }}
      // when user changes focus, publish parsed value
      onBlur={() => {
        setIsFocused(false);
        onValueChange(valueParser(editValue as string));
      }}
    />
  );
}
